import React, { useState, useEffect } from 'react';
import { StarIcon, ClockIcon, CheckIcon } from '@heroicons/react/20/solid';
import { API_BASE } from '@/config/api';
import { getAuthHeaders } from '@/utils/apiUtils';

interface Offer {
  id: number;
  tailor_name: string;
  tailor_rating?: number;
  price: number;
  eta_days: number;
  message?: string;
  expires_at: string;
}

interface OffersTrayProps {
  quoteRequestId: number;
  onAcceptOffer: (offerId: number) => void;
}

const OffersTray: React.FC<OffersTrayProps> = ({ quoteRequestId, onAcceptOffer }) => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOffer, setSelectedOffer] = useState<number | null>(null);

  const API_BASE_URL = API_BASE; // Use from config instead of hardcoding

  const fetchOffers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quotes/requests/${quoteRequestId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch offers');
      }
      const data = await response.json();
      setOffers(data.offers || []);
    } catch (_err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
    // Poll for new offers every 30 seconds
    const interval = setInterval(fetchOffers, 30000);
    return () => clearInterval(interval);
  }, [quoteRequestId]);

  const handleAcceptOffer = async (offerId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quotes/${offerId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to accept offer');
      }

      const result = await response.json();
      onAcceptOffer(offerId);
      alert(`Offer accepted! Order created: ${result.order_number}`);
    } catch (_error) {
      console.error('Error accepting offer:', error);
      alert('Failed to accept offer. Please try again.');
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatExpiryTime = (expiresAt: string) => {
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return 'Expired';
    } else if (diffDays === 1) {
      return 'Expires today';
    } else {
      return `Expires in ${diffDays} days`;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading offers...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button
            onClick={fetchOffers}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-600">
          <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium mb-2">Waiting for offers</h3>
          <p className="text-sm">
            We've sent your request to nearby tailors. 
            You should receive offers within 5-15 minutes.
          </p>
          <button
            onClick={fetchOffers}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Check for new offers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Offers from Tailors ({offers.length})
        </h3>
        <button
          onClick={fetchOffers}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {offers.map((offer) => (
          <div
            key={offer.id}
            className={`border rounded-lg p-4 transition-colors ${
              selectedOffer === offer.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <h4 className="font-medium text-gray-900 mr-2">
                    {offer.tailor_name}
                  </h4>
                  {offer.tailor_rating && (
                    <div className="flex items-center">
                      {renderStars(Math.round(offer.tailor_rating))}
                      <span className="text-sm text-gray-600 ml-1">
                        ({offer.tailor_rating})
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-4 mb-2">
                  <div className="flex items-center text-green-600">
                    <span className="text-lg font-semibold">₹{offer.price}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{offer.eta_days} days</span>
                  </div>
                </div>

                {offer.message && (
                  <p className="text-sm text-gray-600 mb-2">{offer.message}</p>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {formatExpiryTime(offer.expires_at)}
                  </span>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedOffer(offer.id)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        selectedOffer === offer.id
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {selectedOffer === offer.id ? (
                        <>
                          <CheckIcon className="h-4 w-4 inline mr-1" />
                          Selected
                        </>
                      ) : (
                        'Select'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedOffer && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Selected offer from{' '}
              <span className="font-medium">
                {offers.find(o => o.id === selectedOffer)?.tailor_name}
              </span>
            </div>
            <button
              onClick={() => handleAcceptOffer(selectedOffer)}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Accept Offer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersTray;

